import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  type?: 'website' | 'article' | 'local_business';
  noIndex?: boolean;
}

const defaultTitle = 'Family Friends Hibachi - At Home Hibachi Experience';
const defaultDescription =
  'Top Rated Hibachi At Home Experience. We bring our Hibachi Grill + Chef to your backyard. Serving California, Texas, and Florida.';
const defaultKeywords =
  'hibachi, at home hibachi, hibachi catering, backyard hibachi, hibachi chef, California hibachi, Texas hibachi, Florida hibachi';

// JSON-LD 结构化数据
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Family Friends Hibachi',
  url: 'https://familyfriendshibachi.com',
  logo: 'https://familyfriendshibachi.com/icon.svg',
  description: defaultDescription,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-909-615-6633',
    contactType: 'customer service',
    email: 'familyfriendshibachi@gmail.com',
    areaServed: ['US'],
    availableLanguage: ['English', 'Spanish', 'Chinese'],
  },
  sameAs: [
    'https://instagram.com/familyfriendshibachi',
    'https://facebook.com/familyfriendshibachi',
    'https://tiktok.com/@familyfriendshibachi',
  ],
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'FoodService',
  name: 'Family Friends Hibachi',
  image: 'https://familyfriendshibachi.com/icon.svg',
  '@id': 'https://familyfriendshibachi.com',
  url: 'https://familyfriendshibachi.com',
  telephone: '+1-909-615-6633',
  email: 'familyfriendshibachi@gmail.com',
  priceRange: '$$',
  servesCuisine: 'Japanese, Hibachi',
  description: defaultDescription,
  areaServed: [
    { '@type': 'State', name: 'California' },
    { '@type': 'State', name: 'Texas' },
    { '@type': 'State', name: 'Florida' },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Hibachi Catering Menu',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'At-Home Hibachi Experience',
          description: 'Professional hibachi chef comes to your location with all equipment',
        },
      },
    ],
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    bestRating: '5',
    worstRating: '1',
    ratingCount: '500',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Family Friends Hibachi',
  url: 'https://familyfriendshibachi.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://familyfriendshibachi.com/?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

/**
 * SEO component for managing document head
 * Uses react-helmet-async for SSR compatibility
 * Includes JSON-LD structured data for better search engine understanding
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description = defaultDescription,
  keywords = defaultKeywords,
  ogImage,
  ogUrl,
  type = 'website',
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} | Family Friends Hibachi` : defaultTitle;
  const baseUrl = 'https://familyfriendshibachi.com';

  // 选择适当的结构化数据
  const getStructuredData = (): object[] => {
    const schemas: object[] = [organizationSchema, websiteSchema];
    if (type === 'local_business') {
      schemas.push(localBusinessSchema);
    }
    return schemas;
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={ogUrl || baseUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'local_business' ? 'business.business' : 'website'} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="Family Friends Hibachi" />
      <meta property="og:locale" content="en_US" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* JSON-LD Structured Data */}
      {getStructuredData().map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;


